import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';

import { TimeTrack, ViewMode } from '../types/time-tracking';

const TIME_TRACKING_STORAGE_KEY = 'time-tracking-ui-state';

/**
 * What the week-view totals row uses as the progress-bar denominator.
 * "availability" → daily/weekly capacity minus approved time-off.
 * "booked" → total booked hours for the same period.
 */
export enum WeekProgressBasis {
  Availability = 'availability',
  Booked = 'booked',
}

interface PersistedState {
  viewMode: ViewMode;
  expandedProjects: Record<number, boolean>;
  focusedProjectDay: Record<number, string | null>;
  expandedTimeOff: boolean;
  weekProgressBasis: WeekProgressBasis;
  activeTimerProject: DropdownSearchItem | null;
}

interface TimeTrackingState {
  /** Last-used view mode. Acts as a fallback when the URL has no `?view` param. */
  viewMode: ViewMode;

  /** Collapsed/expanded state for projects in the week/day list. */
  expandedProjects: Record<number, boolean>;

  /**
   * Week-view per-project "focused day". When a specific day cell is clicked,
   * only that day's entries are expanded (keyed `YYYY-MM-DD`); `null`/absent
   * means the row was expanded via the project name → all days are shown
   * (OPS-1565).
   */
  focusedProjectDay: Record<number, string | null>;

  /** Collapsed/expanded state for the week-view time-off row (single row → boolean). */
  expandedTimeOff: boolean;

  /** Time tracks indexed by ID (currently unused — kept for future use). */
  timeTracks: Record<number, TimeTrack>;

  /** Project preselected for the next timer start (shared across TimeTracker instances). */
  preselectedProject: DropdownSearchItem | null;

  /** Description preselected for the next timer start. */
  preselectedDescription: string;

  /**
   * Project of the currently-running timer, persisted so the widget can render
   * its name/color without re-fetching full project details (which 403s for
   * worklog-only users). Set when the timer starts; cleared when it stops/discards.
   */
  activeTimerProject: DropdownSearchItem | null;

  /** Selected basis for the Week-view "Day total" progress bars. */
  weekProgressBasis: WeekProgressBasis;

  // Actions
  setViewMode: (mode: ViewMode) => void;
  toggleProjectExpanded: (projectId: number) => void;
  /** Expand a project row showing ALL days (project-name click) — clears the focused day. */
  expandProjectAllDays: (projectId: number) => void;
  /**
   * Toggle a single day for a project (day-cell click). Opens the row focused on
   * `dateKey`; clicking a different day switches focus; clicking the already-
   * focused day collapses the row.
   */
  toggleProjectDay: (projectId: number, dateKey: string) => void;
  toggleTimeOffExpanded: () => void;
  setPreselectedProject: (project: DropdownSearchItem | null) => void;
  setPreselectedDescription: (description: string) => void;
  setActiveTimerProject: (project: DropdownSearchItem | null) => void;
  clearPreselectedTimerState: () => void;
  getTimeTrackById: (id: number) => TimeTrack | undefined;
  setWeekProgressBasis: (basis: WeekProgressBasis) => void;
}

export const useTimeTrackingStore = create<TimeTrackingState>()(
  persist(
    (set, get) => ({
      viewMode: ViewMode.Week,
      expandedProjects: {},
      focusedProjectDay: {},
      expandedTimeOff: false,
      timeTracks: {},
      preselectedProject: null,
      preselectedDescription: '',
      activeTimerProject: null,
      weekProgressBasis: WeekProgressBasis.Booked,

      setViewMode: (mode: ViewMode) => set({ viewMode: mode }),

      toggleProjectExpanded: (projectId: number) =>
        set(state => ({
          expandedProjects: {
            ...state.expandedProjects,
            [projectId]: !state.expandedProjects[projectId],
          },
        })),

      expandProjectAllDays: (projectId: number) =>
        set(state => {
          // Project-name click: toggle the row, and when opening, clear any
          // focused day so all days show (OPS-1565).
          const willExpand = !state.expandedProjects[projectId];
          return {
            expandedProjects: { ...state.expandedProjects, [projectId]: willExpand },
            focusedProjectDay: { ...state.focusedProjectDay, [projectId]: null },
          };
        }),

      toggleProjectDay: (projectId: number, dateKey: string) =>
        set(state => {
          const isExpanded = state.expandedProjects[projectId] ?? false;
          const focusedDay = state.focusedProjectDay[projectId] ?? null;
          // Clicking the already-focused day while open collapses the row.
          if (isExpanded && focusedDay === dateKey) {
            return {
              expandedProjects: { ...state.expandedProjects, [projectId]: false },
              focusedProjectDay: { ...state.focusedProjectDay, [projectId]: null },
            };
          }
          // Otherwise open the row focused on the clicked day (switching focus
          // from any previously open day).
          return {
            expandedProjects: { ...state.expandedProjects, [projectId]: true },
            focusedProjectDay: { ...state.focusedProjectDay, [projectId]: dateKey },
          };
        }),

      toggleTimeOffExpanded: () => set(state => ({ expandedTimeOff: !state.expandedTimeOff })),

      setPreselectedProject: (project: DropdownSearchItem | null) =>
        set({ preselectedProject: project }),

      setPreselectedDescription: (description: string) =>
        set({ preselectedDescription: description }),

      setActiveTimerProject: (project: DropdownSearchItem | null) =>
        set({ activeTimerProject: project }),

      clearPreselectedTimerState: () =>
        set({ preselectedProject: null, preselectedDescription: '' }),

      getTimeTrackById: (id: number) => {
        return get().timeTracks[id];
      },

      setWeekProgressBasis: (basis: WeekProgressBasis) => set({ weekProgressBasis: basis }),
    }),
    {
      name: TIME_TRACKING_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state): PersistedState => ({
        viewMode: state.viewMode,
        expandedProjects: state.expandedProjects,
        focusedProjectDay: state.focusedProjectDay,
        expandedTimeOff: state.expandedTimeOff,
        weekProgressBasis: state.weekProgressBasis,
        activeTimerProject: state.activeTimerProject,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<PersistedState>;
        return {
          ...currentState,
          viewMode: persisted.viewMode ?? currentState.viewMode,
          expandedProjects: persisted.expandedProjects ?? currentState.expandedProjects,
          focusedProjectDay: persisted.focusedProjectDay ?? currentState.focusedProjectDay,
          expandedTimeOff: persisted.expandedTimeOff ?? currentState.expandedTimeOff,
          weekProgressBasis: persisted.weekProgressBasis ?? currentState.weekProgressBasis,
          activeTimerProject: persisted.activeTimerProject ?? currentState.activeTimerProject,
        };
      },
    }
  )
);
